import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { PDFDocumentProxy, getDocument as pdfjsGetDocument } from 'pdfjs-dist';
import { PDFViewer } from 'pdfjs-dist/web/pdf_viewer';

export interface PdfjsViewerComponentToolbarI18n {
  previousPageButtonTitle?: string;
  previousPageButtonLabel?: string;
  nextPageButtonTitle?: string;
  nextPageButtonLabel?: string;
  zoomOutButtonTitle?: string;
  zoomOutButtonLabel?: string;
  zoomInButtonTitle?: string;
  zoomInButtonLabel?: string;
  downloadPdfButtonTitle?: string;
  downloadPdfButtonLabel?: string;
  pageXOfYTitleFormat?: string;
  pageXOfYLabelFormat?: string;
}

export interface PdfjsViewerComponentI18n {
  [languageCode: string]: {
    toolbar?: PdfjsViewerComponentToolbarI18n;
  };
}

export interface ToolbarButtonEvent extends MouseEvent {
  readonly toolbarButton: string;
}

// tslint:disable-next-line:max-line-length
export type PdfjsViewerComponentScaleValue = number | 'page-actual' | 'page-width' | 'page-height' | 'page-fit' | 'auto';

const MIN_SCALE = 0.2;
const MAX_SCALE = 10.0;
const DEFAULT_SCALE_DELTA = 1.1;

let componentId = 0;

type PagesLoadedEvent = CustomEvent<{ pagesCount: number; }>;
type PageChangeEvent = UIEvent & { pageNumber: number; };

@Component({
  selector: 'app-pdfjs-viewer',
  templateUrl: 'pdfjs-viewer.component.html',
  styleUrls: ['pdfjs-viewer.component.scss']
})
export class PdfjsViewerComponent implements AfterViewInit, OnDestroy {
  static readonly IdPrefixPdfjsComponent = 'pdfjs-component-';
  static readonly IdPrefixPdfjsContainer = 'pdfjs-container-';
  static readonly IdPrefixPdfjsViewer = 'pdfjs-viewer-';

  static readonly ClassNameContainerDiv = 'pdfjs-container';
  static readonly ClassNameViewerDiv = 'pdfjs-viewer';
  static readonly ClassNameToolbar = 'pdfjs-toolbar';
  static readonly ClassNameToolbarContainer = 'pdfjs-toolbar-container';
  static readonly ClassNameToolbarViewer = 'pdfjs-toolbar-viewer';
  static readonly ClassNameToolbarViewerLeft = 'pdfjs-toolbar-viewer-left';
  static readonly ClassNameToolbarViewerMiddle = 'pdfjs-toolbar-viewer-middle';
  static readonly ClassNameToolbarViewerRight = 'pdfjs-toolbar-viewer-right';
  static readonly ClassNameToolbarButtonGroup = 'pdfjs-toolbar-btn-group';
  static readonly ClassNameToolbarButton = 'pdfjs-toolbar-btn';
  static readonly ClassNameToolbarButtonLabel = 'pdfjs-toolbar-btn-label btn-label-color';
  static readonly ClassNameToolbarButtonSeparator = 'btn-separator';
  static readonly ClassNameToolbarButtonPreviousPage = 'btn-previous-page';
  static readonly ClassNameToolbarButtonNextPage = 'btn-next-page';
  static readonly ClassNameToolbarButtonZoomOut = 'btn-zoom-out';
  static readonly ClassNameToolbarButtonZoomIn = 'btn-zoom-in';
  static readonly ClassNameToolbarButtonDownloadPdf = 'btn-download-pdf';

  static i18n: PdfjsViewerComponentI18n = { // NON-readonly on purpose
    en: {
      toolbar: {
        previousPageButtonLabel: 'Previous page',
        nextPageButtonLabel: 'Next page',
        zoomOutButtonLabel: 'Zoom out',
        zoomInButtonLabel: 'Zoom in',
        downloadPdfButtonLabel: 'Download PDF',
        pageXOfYTitleFormat: 'Page {pageNumber} of {pageCount}',
        pageXOfYLabelFormat: '{pageNumber} / {pageCount}'
      }
    },
    fr: {
      toolbar: {
        previousPageButtonLabel: 'Page précédente',
        nextPageButtonLabel: 'Page suivante',
        zoomOutButtonLabel: 'Zoom arrière',
        zoomInButtonLabel: 'Zoom avant',
        downloadPdfButtonLabel: 'Télécharger PDF',
        pageXOfYTitleFormat: 'Page {pageNumber} sur {pageCount}',
        pageXOfYLabelFormat: '{pageNumber} / {pageCount}'
      }
    }
  };

  static DefaultLanguageCode = 'en'; // NON-readonly on purpose

  @Input() source: string;
  @Input() languageCode: string;
  // tslint:disable:no-input-rename
  @Input('componentDivClass') componentClass: string = '';
  @Input('containerDivClass') containerClass: string = '';
  @Input('viewerDivClass') viewerClass: string = '';
  @Input('toolbarButtonClass') buttonClass: string = '';
  // tslint:enable:no-input-rename

  @Output() documentLoadComplete = new EventEmitter<PDFDocumentProxy>();
  @Output() documentLoadError = new EventEmitter<any>();
  @Output() pagesLoaded = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() toolbarButtonClick = new EventEmitter<ToolbarButtonEvent>();

  private readonly _componentDivId: string;
  private readonly _containerDivId: string;
  private readonly _viewerDivId: string;
  private readonly _elementRefCache: { [selectors: string]: HTMLElement | null };

  private _viewer: PDFViewer | null = null;
  private _document: PDFDocumentProxy | null = null;
  private _pageCount = 0;
  private _pageNumber = 0;

  constructor() {
    let nextId = (++componentId).toString(10);
    if (componentId >= 0 && componentId < 10) {
      nextId = '0' + nextId;
    }

    this._componentDivId = PdfjsViewerComponent.IdPrefixPdfjsComponent + nextId;
    this._containerDivId = PdfjsViewerComponent.IdPrefixPdfjsContainer + nextId;
    this._viewerDivId = PdfjsViewerComponent.IdPrefixPdfjsViewer + nextId;
    this._elementRefCache = {};

    this.onDocumentLoadComplete = this.onDocumentLoadComplete.bind(this);
    this.onDocumentLoadError = this.onDocumentLoadError.bind(this);
    this.onPagesLoaded = this.onPagesLoaded.bind(this);
    this.onPageChange = this.onPageChange.bind(this);

    this.onPreviousPageButtonClick = this.onPreviousPageButtonClick.bind(this);
    this.onNextPageButtonClick = this.onNextPageButtonClick.bind(this);
    this.onZoomOutButtonClick = this.onZoomOutButtonClick.bind(this);
    this.onZoomInButtonClick = this.onZoomInButtonClick.bind(this);
    this.onDownloadPdfButtonClick = this.onDownloadPdfButtonClick.bind(this);
  }

  //#region Element ID and class name getters
  get componentDivId(): string { return this._componentDivId; }
  get componentDivClass(): string { return this.componentClass; }

  get containerDivId(): string { return this._containerDivId; }
  get containerDivClass(): string {
    return [
      PdfjsViewerComponent.ClassNameContainerDiv,
      this.containerClass
    ].join(' ').trim();
  }

  get viewerDivId(): string { return this._viewerDivId; }
  get viewerDivClass(): string {
    return [
      PdfjsViewerComponent.ClassNameViewerDiv,
      'pdfViewer',
      'removePageBorders',
      this.viewerClass
    ].join(' ').trim();
  }

  get toolbarClass(): string { return PdfjsViewerComponent.ClassNameToolbar; }
  get toolbarContainerClass(): string { return PdfjsViewerComponent.ClassNameToolbarContainer; }
  get toolbarViewerClass(): string { return PdfjsViewerComponent.ClassNameToolbarViewer; }
  get toolbarViewerLeftClass(): string { return PdfjsViewerComponent.ClassNameToolbarViewerLeft; }
  get toolbarViewerMiddleClass(): string { return PdfjsViewerComponent.ClassNameToolbarViewerMiddle; }
  get toolbarViewerRightClass(): string { return PdfjsViewerComponent.ClassNameToolbarViewerRight; }
  get toolbarButtonGroupClass(): string { return PdfjsViewerComponent.ClassNameToolbarButtonGroup; }

  get toolbarButtonClass(): string {
    return [
      PdfjsViewerComponent.ClassNameToolbarButton,
      this.buttonClass
    ].join(' ').trim();
  }

  get toolbarButtonLabelClass(): string { return PdfjsViewerComponent.ClassNameToolbarButtonLabel; }
  get toolbarButtonSeparatorClass(): string { return PdfjsViewerComponent.ClassNameToolbarButtonSeparator; }

  get toolbarButtonPreviousPageClass(): string {
    return [
      this.toolbarButtonClass,
      PdfjsViewerComponent.ClassNameToolbarButtonPreviousPage
    ].join(' ').trim();
  }

  get toolbarButtonNextPageClass(): string {
    return [
      this.toolbarButtonClass,
      PdfjsViewerComponent.ClassNameToolbarButtonNextPage
    ].join(' ').trim();
  }

  get toolbarButtonZoomOutClass(): string {
    return [
      this.toolbarButtonClass,
      PdfjsViewerComponent.ClassNameToolbarButtonZoomOut
    ].join(' ').trim();
  }

  get toolbarButtonZoomInClass(): string {
    return [
      this.toolbarButtonClass,
      PdfjsViewerComponent.ClassNameToolbarButtonZoomIn
    ].join(' ').trim();
  }

  get toolbarButtonDownloadPdfClass(): string {
    return [
      this.toolbarButtonClass,
      PdfjsViewerComponent.ClassNameToolbarButtonDownloadPdf
    ].join(' ').trim();
  }
  //#endregion

  get viewer(): PDFViewer | null { return this._viewer; }

  get pageCount(): number {
    return this._pageCount;
  }

  get pageNumber(): number {
    return this._pageNumber;
  }

  set pageNumber(value: number) {
    this.scrollPageIntoView({ pageNumber: value });
  }

  get currentScale(): number {
    return this._viewer == null ? NaN : this._viewer.currentScale;
  }

  set currentScale(value: number) {
    if (this._viewer != null) {
      this._viewer.currentScale = value;
    }
  }

  get currentScaleValue(): PdfjsViewerComponentScaleValue {
    return this._viewer == null ? NaN : this._viewer.currentScaleValue;
  }

  set currentScaleValue(value: PdfjsViewerComponentScaleValue) {
    if (this._viewer != null) {
      this._viewer.currentScaleValue = value;
    }
  }

  ngAfterViewInit(): void {
    window.addEventListener('updateviewarea', (): void => { this.currentScaleValue = 'auto'; }, { once: true });
    window.addEventListener('pagesloaded', this.onPagesLoaded);
    window.addEventListener('pagechange', this.onPageChange);

    this.getDocument();
  }

  ngOnDestroy(): void {
    window.removeEventListener('pagesloaded', this.onPagesLoaded);
    window.removeEventListener('pagechange', this.onPageChange);
    if (this._document != null) {
      this._document.destroy();
    }
  }

  i18n(path: string, languageCode?: string): string {
    let i18nObj = PdfjsViewerComponent.i18n[languageCode];
    if (i18nObj == null) {
      i18nObj = PdfjsViewerComponent.i18n[PdfjsViewerComponent.DefaultLanguageCode];
    }

    let value = '';
    if (i18nObj != null) {
      value = path.split('.').reduce((obj, key) => obj[key], i18nObj);
      if (value == null) {
        value = '';
        if (languageCode !== PdfjsViewerComponent.DefaultLanguageCode) {
          value = this.i18n(path, PdfjsViewerComponent.DefaultLanguageCode);
        }
      }
    }

    return value;
  }

  /**
   * @access protected
   */
  getButtonLabel(button: string): string {
    return this.i18n(`toolbar.${button}ButtonLabel`);
  }

  /**
   * @access protected
   */
  getButtonTitle(button: string): string {
    return this.i18n(`toolbar.${button}ButtonTitle`) || this.getButtonLabel(button);
  }

  /**
   * @access protected
   */
  get pageXOfYLabel(): string {
    const format = this.i18n('toolbar.pageXOfYLabelFormat');
    return format
      .replace('{pageCount}', this._pageCount.toString(10))
      .replace('{pageNumber}', this._pageNumber.toString(10));
  }

  /**
   * @access protected
   */
  get pageXOfYTitle(): string {
    const format = this.i18n('toolbar.pageXOfYTitleFormat');
    if (typeof format === 'string') {
      return format
        .replace('{pageCount}', this._pageCount.toString(10))
        .replace('{pageNumber}', this._pageNumber.toString(10));
    }
    return this.pageXOfYLabel;
  }

  scrollPageIntoView(params: { pageNumber: number; }): void {
    if (document && document.querySelectorAll) {
      const pageList = Array.from(document.querySelectorAll(`#${this.viewerDivId} > .page`));
      const targetPage = pageList.find((page: HTMLDivElement): boolean => {
        return page.dataset.pageNumber === params.pageNumber.toString();
      });
      if (targetPage != null) {
        targetPage.scrollIntoView({ block: 'start', behavior: 'smooth' });
        this._pageNumber = params.pageNumber;
      }
    }
  }

  /**
   * @access protected
   */
  onPreviousPageButtonClick(event: MouseEvent): void {
    if (this.emitToolbarButtonEvent('previousPage', event) && !event.defaultPrevented) {
      this.scrollPageIntoView({ pageNumber: this._pageNumber - 1 });
    }
  }

  /**
   * @access protected
   */
  onNextPageButtonClick(event: MouseEvent): void {
    if (this.emitToolbarButtonEvent('nextPage', event) && !event.defaultPrevented) {
      this.scrollPageIntoView({ pageNumber: this._pageNumber + 1 });
    }
  }

  /**
   * @access protected
   */
  onZoomOutButtonClick(event: MouseEvent): void {
    if (this.emitToolbarButtonEvent('zoomOut', event) && !event.defaultPrevented) {
      let newScale = this._viewer.currentScale;
      newScale = +((newScale / DEFAULT_SCALE_DELTA).toFixed(2));
      newScale = Math.floor(newScale * 10) / 10;
      newScale = Math.max(MIN_SCALE, newScale);
      this.currentScaleValue = newScale;
    }
  }

  /**
   * @access protected
   */
  onZoomInButtonClick(event: MouseEvent): void {
    if (this.emitToolbarButtonEvent('zoomIn', event) && !event.defaultPrevented) {
      let newScale = this._viewer.currentScale;
      newScale = +((newScale * DEFAULT_SCALE_DELTA).toFixed(2));
      newScale = Math.ceil(newScale * 10) / 10;
      newScale = Math.min(MAX_SCALE, newScale);
      this.currentScaleValue = newScale;
    }
  }

  /**
   * @access protected
   */
  onDownloadPdfButtonClick(event: MouseEvent): void {
    this.emitToolbarButtonEvent('downloadPdf', event);
  }

  // protected getElementRef<T extends HTMLElement>(selectors: string): T | null {
  //     let elementRef = this._elementRefCache[selectors] as T;
  //     if (elementRef == null) {
  //         elementRef = document.querySelector<T>(selectors);
  //         if (elementRef != null) {
  //             this._elementRefCache[selectors] = elementRef;
  //         }
  //     }
  //     return elementRef;
  // }

  protected getDocument(): void {
    if (typeof this.source === 'string' && this._document == null) {
      const loadingTask = pdfjsGetDocument(this.source);
      loadingTask.then(this.onDocumentLoadComplete, this.onDocumentLoadError);
    }
  }

  protected renderDocument(): void {
    if (this._document != null && this._viewer == null) {
      this._viewer = new PDFViewer({
        container: document.getElementById(this.containerDivId) as HTMLDivElement,
        viewer: document.getElementById(this.viewerDivId) as HTMLDivElement,
        textLayerMode: 0,
        removePageBorders: true,
        enableWebGL: true
      });
      this._viewer.setDocument(this._document);
    }
  }

  protected onDocumentLoadComplete(document: PDFDocumentProxy): void {
    this._document = document;
    this.documentLoadComplete.emit(document);
    this.renderDocument();
  }

  protected onDocumentLoadError(reason: any): void {
    this.documentLoadError.emit(reason);
  }

  protected onPagesLoaded(e: PagesLoadedEvent): void {
    this._pageCount = e.detail.pagesCount;
    this._pageNumber = 1;
    this.pagesLoaded.emit(this._pageCount);
  }

  protected onPageChange(e: PageChangeEvent): void {
    this._pageNumber = e.pageNumber;
    this.pageChange.emit(this._pageNumber);
  }

  protected emitToolbarButtonEvent(button: string, event: MouseEvent): boolean {
    if (this._viewer != null && this._pageCount > 0) {
      this.toolbarButtonClick.emit(Object.assign({}, { toolbarButton: button }, event));
      return true;
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/global/pdfjs-viewer.component.ts